import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

export default function ListSitemap({ refresh, setRefresh, chatbotId, permission }) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchingText, setFetchingText] = useState("");
  const [fetchingIcon, setFetchingIcon] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Sitemap Lists | KlonIT Web Application";
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/get-sitemap`, { params });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setDocuments(responseData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching Sitemap:", error);
      setError("Error fetching Sitemap.");
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.delete(`/delete-sitemap/${documentId}`, { params });
      setDocuments(documents.filter((doc) => doc.id !== documentId));
      setError("Sitemap has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting Sitemap:", error);
      setError("Error deleting Sitemap.");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600 }}
          color="textPrimary"
        >
          Sitemap List
        </Typography>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : documents.length === 0 ? (
          <Typography variant="body1" color="textSecondary" align="center">
            No data available
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                  <TableCell sx={{ fontWeight: 600 }}>Sitemaps</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Create Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>{document.sitemap}</TableCell>
                    <TableCell>{document.created_date}</TableCell>
                    <TableCell>
                      {permission.canDelete && (
                        <Tooltip title="Delete Sitemap" arrow>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(document.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {error && (
          <Alert
            severity={
              error.includes("deleted successfully") ? "success" : "warning"
            }
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        )}
      </Paper>

      {/* Fetching Modal */}
      <Dialog
        open={fetching}
        onClose={() => setFetching(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            textAlign: "center",
            p: 3,
            borderRadius: 2,
          },
        }}
      >
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <i className={fetchingIcon} style={{ fontSize: 32 }}></i>
            <Typography>{fetchingText || "Fetching Data..."}</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
